// --------------------------------------------------------
// REGISTER EXAM - RegisterExam
// --------------------------------------------------------

// --------------------------------------------------------
// IMPORTS
// --------------------------------------------------------
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../..";
import ErrorArea from "../../../../components/error/ErrorArea";
import Radios from "../../../../components/radios/Radios";
import ContainerWithSidebar from "../../../../components/UI/containerWithSidebar/ContainerWithSidebar";
import {
  GeneralContainer,
  H1,
  H2,
  SubTitleSimple,
  Text,
} from "../../../../components/UI/generalLayout/Layout.styled";
import { Spacer } from "../../../../components/UI/spacer/spacer";
import Colors from "../../../../constants/Colors";
import { removeFieldFromList } from "../../../../helpers/CareLineHelperFunctions";
import { handleBackendErrors } from "../../../../helpers/HelperFunctions";
import useFetchCareLinesOccupHealthHelper from "../../../../hooks/useFetchCareLinesOccupHealthHelper";
import Exam from "../../../../models/core/careLine/Exam";
import CardLineSelected from "./components/cardLineSelected/CardLineSelected";
import AdditionalExams from "./components/modalExams/AdditionalExams";
import {
  ModalExamsTextCardContainer,
  ModalExamsButtonsContainer,
} from "./components/modalExams/ModalExams.styled";
import * as careLineActions from "../../../../store/actions/careLine";
import * as examActions from "../../../../store/actions/exams";
import { Checkbox } from "@mui/material";
import { Button } from "../../../company/companyRegister/CompanyRegister.styled";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useLocation, useNavigate } from "react-router-dom";
import { Paths } from "../../../../enums/Paths";

// --------------------------------------------------------
// REGISTER EXAM INTERFACE
// --------------------------------------------------------
/**
 * A component that renders a Screen.
 *
 * @param props Properties
 * @param props.route The route
 * @param props.navigation The navigator
 * @returns The component JSX.
 */
function RegisterExam(): JSX.Element {
  // --------------------------------------------------------
  // REGISTER EXAM PRIVATE DECLARATIONS
  // --------------------------------------------------------
  const navigate = useNavigate();
  const { state }: any = useLocation();
  const { pessoa } = !!state ? state : { pessoa: { idPessoa: "", nome: "" } };
  const [optionSelected, setOptionSelected] = useState("");
  const [careLinesList, setCareLines] = useState<any[]>([]);
  const [selectedCareLinesOccupHealth, setSelectedCareLinesOccupHealth] =
    useState<any[]>([]);
  const [occupHealth, setOccupHealth] = useState<any[]>([]);
  const [isLoadingExams, setIsLoadingExams] = useState<boolean>(false);
  const [invalidDate, setInvalidDate] = useState<boolean>(false);
  const [availableExams, setAvailableExams] = useState<any[]>([]);
  const [extraExams, setExtraExams] = useState<any[]>([]);
  const [results, setExamsResult] = useState<any[]>([]);
  const [errors, setErrors] = useState<any[]>([]);

  const dispatch = useAppDispatch();

  const { fetchPersonCareLines, fetchPersonOccupHealth } =
    useFetchCareLinesOccupHealthHelper(pessoa);
  const { careLines, allExams } = useSelector((state: any) => state.careLine);

  const fetchAllCareLines = useCallback(async () => {
    await dispatch(careLineActions.fetchDropdownData());
    await dispatch(careLineActions.fetchActiveCareLines());
  }, []);

  const getCareLines = useCallback(async () => {
    const careLinesFetch: any[] = (await fetchPersonCareLines()) ?? [];
    const careLinesState: any[] = [];
    careLinesFetch.forEach((c) => {
      const item = careLines.filter((cl: any) => cl.id === c.id);
      item.length > 0 && careLinesState.push(item[0]);
    });
    setCareLines(careLinesState);
  }, [careLines]);

  const onNavigateToProntuario = () => {
    navigate(Paths.Prontuario, { state: { pessoa: pessoa } });
  };

  const onCancel = () => {
    setOptionSelected("");
    setSelectedCareLinesOccupHealth([]);
    setAvailableExams([]);
    setErrors([]);
    setExtraExams([]);
    onNavigateToProntuario();
  };

  const getOccupHealth = useCallback(async () => {
    const careLines: any[] = (await fetchPersonOccupHealth()) ?? [];
    setOccupHealth(careLines);
  }, []);

  useEffect(() => {
    fetchAllCareLines();
    getCareLines();
    getOccupHealth();
  }, []);

  useEffect(() => {
    setIsLoadingExams(true);
    const availableExams: any[] = [];
    selectedCareLinesOccupHealth.forEach((item: any) => {
      const careLineOcp = selectedCareLinesOccupHealth.filter(
        (e) => e.id === item.id
      );
      careLineOcp[0].exames.forEach((exame: any) => {
        const examIndex = availableExams.findIndex((exam: any) => {
          return exam.id === exame.id;
        });
        if (examIndex === -1) {
          const examToInsert: any[] = allExams.filter(
            (ex: Exam) => ex.id === exame.id
          );
          availableExams.push({ ...examToInsert[0], formato: exame.formato });
        }
      });
    });

    setAvailableExams(availableExams);
    setIsLoadingExams(false);
  }, [selectedCareLinesOccupHealth]);

  //   Tipos de Exames
  const [options] = useState([
    {
      label: "Linha de cuidado",
      value: "linhaDeCuidado",
    },
    {
      label: "Saúde ocupacional",
      value: "saudeOcupacional",
    },
  ]);

  const ExamsCareLineSelectionArea = () => {
    const listaRetorno =
      optionSelected === "linhaDeCuidado"
        ? [...careLinesList]
        : [...occupHealth];
    return (
      <>
        {listaRetorno.map((element: any) => {
          return (
            <div
              style={{
                flexDirection: "row",
                display: "flex",
                marginBottom: -15,
              }}
            >
              <Checkbox
                checked={
                  selectedCareLinesOccupHealth.findIndex(
                    (el) => el.id === element.id
                  ) !== -1
                }
                onChange={() => {
                  setSelectedCareLinesOccupHealth((prev: any) => {
                    const list = [...prev];
                    const index = list.findIndex((el) => el.id === element.id);
                    index === -1 ? list.push(element) : list.splice(index, 1);
                    return list;
                  });
                }}
              />
              <Text>{element.nome}</Text>
            </div>
          );
        })}
      </>
    );
  };

  const mergeAdditionalResults = () => {
    extraExams.forEach((ee) => {
      const object = {
        dataColeta: ee.dataColeta,
        exameId: ee.nome.id,
        formato: ee.tipo,
        texto: ee.tipo === "Texto" ? ee.resultado.valor : "",
        valor: ee.tipo === "Numerico" ? ee.resultado.valor : "",
        unidade: ee.tipo === "Numerico" ? ee.resultado.unidade : "",
      };
      results.push(object);
    });
    return results;
  };

  const saveExamResults = async () => {
    const resultados = mergeAdditionalResults();
    const isSomeObjectEmpty = resultados.some((el: any) => {
      if (el.formato === "Numerico") {
        return el.dataColeta === "" || el.valor === "" || el.unidade === "";
      } else {
        return el.dataColeta === "" || el.texto === "";
      }
    });
    if (isSomeObjectEmpty) {
      setErrors([
        { message: "Adicione resultados para todos os exames selecionados" },
      ]);
    } else {
      try {
        await dispatch(
          examActions.registerExamResult(resultados, pessoa.idPessoa)
        );
        onNavigateToProntuario();
      } catch (error: any) {
        handleBackendErrors(error, setErrors);
      }
    }
  };

  const getVariableHeight = () => {
    if (optionSelected === "linhaDeCuidado") {
      return careLinesList.length > 2 ? 200 : 80;
    } else if (optionSelected === "saudeOcupacional") {
      return occupHealth.length > 2 ? 200 : 80;
    }
  };

  return (
    <ContainerWithSidebar>
      <GeneralContainer>
        <div>
          <ModalExamsTextCardContainer>
            <H1
              style={{
                fontSize: "28px",
              }}
              color={Colors.SPM600}
            >
              {"Novo registro de exame"}
            </H1>
            <ErrorArea
              errorList={errors}
              onCloseAction={() => {
                setErrors([]);
              }}
            />
          </ModalExamsTextCardContainer>
          <Spacer bottom={10} />
          <div>
            {/* _______________________________________________ */}
            <H2 color={Colors.SPM800}>{"Tipos de exames"}</H2>
            <SubTitleSimple style={{ position: "relative", bottom: "10px" }}>
              {"Selecione qual das opções abaixo deseja registrar os exames."}
            </SubTitleSimple>
            {/* Opções de exames */}
            <Radios
              itens={options}
              selecetedValue={(value: any) => {
                setOptionSelected(value);
                setSelectedCareLinesOccupHealth([]);
              }}
            />
            {/* _______________________________________________ */}
            {optionSelected !== "" && (
              <ModalExamsTextCardContainer>
                <Text bold color={Colors.GRAY800}>
                  {`Selecione ${
                    optionSelected === "linhaDeCuidado"
                      ? "linhas de cuidado"
                      : "saúdes ocupacionais"
                  }`}
                  <span style={{ color: "red" }}>*</span>
                </Text>
                <div
                  style={{
                    overflow: "auto",
                    height: getVariableHeight(),
                    width: "80%",
                  }}
                >
                  <ExamsCareLineSelectionArea />
                </div>
              </ModalExamsTextCardContainer>
            )}
            {/* _______________________________________________ */}
            <H2 color={Colors.SPM800}>{"Exame(s) selecionado(s)"}</H2>
            <SubTitleSimple style={{ position: "relative", bottom: "10px" }}>
              {
                "Selecione exames que deseja registrar na(s) linha(s) de cuidado que foram selecionada(s) anteriormente."
              }
            </SubTitleSimple>

            {availableExams.map((exam) => {
              return (
                <CardLineSelected
                  exam={exam}
                  setExamIntoList={setExamsResult}
                  examList={results}
                  setInvalidDate={setInvalidDate}
                />
              );
            })}

            {/* _______________________________________________ */}
            <H2 color={Colors.SPM800}>{"Exames adicionais"}</H2>
            <SubTitleSimple style={{ position: "relative", bottom: "10px" }}>
              {`Registre exames solicitados anteriormente ou adicione outro tipo
              de exame que não tenha sido solicitado.`}
            </SubTitleSimple>
            {extraExams.map((extraExam: any, index: number) => {
              return (
                <AdditionalExams
                  index={index}
                  setInvalidDate={setInvalidDate}
                  allExams={allExams}
                  additionalExam={extraExam}
                  setAdditionalResult={setExtraExams}
                  onDeleteOption={() => {
                    setExtraExams(removeFieldFromList(extraExams, index));
                  }}
                />
              );
            })}
          </div>
          <Button
            onClick={() => {
              setExtraExams((prev: any) => {
                const list = prev.length === 0 ? [] : [...prev];
                list.push({
                  nome: "",
                  tipo: "",
                  dataColeta: "",
                  resultado: { valor: "", unidade: "" },
                });
                return list;
              });
            }}
            color={Colors.lightBlue}
            backgroundColor={Colors.WHITE}
            borderColor={Colors.lightBlue}
          >
            <AddCircleOutlineIcon />
            {"Adicionar outro tipo de exame"}
          </Button>
          <Spacer bottom={30} />
          <ModalExamsButtonsContainer>
            <Button
              onClick={onCancel}
              color={Colors.lightBlue}
              backgroundColor={Colors.WHITE}
              borderColor={Colors.lightBlue}
            >
              Voltar
            </Button>
            <Button
              onClick={saveExamResults}
              color={Colors.WHITE}
              backgroundColor={Colors.SPM600}
              disabled={
                (availableExams.length === 0 && extraExams.length === 0) ||
                invalidDate
              }
            >
              Salvar registro
            </Button>
          </ModalExamsButtonsContainer>
        </div>
      </GeneralContainer>
    </ContainerWithSidebar>
  );
}

export default RegisterExam;
